/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { MultiSelectProps } from '../../../types/InputComponentProps';
import { InputContainer, selectTriggerStyles, selectContentStyles, selectItemBaseStyles } from './FormInputContainer';
import { Select, createListCollection, Portal, Separator, Box, VStack } from 'chakra-v3';
import { useMemo } from 'react';
import { FaRegCheckSquare, FaRegSquare } from 'react-icons/fa';
import { Text } from '../../../../../components/Index';

export function MultiSelectInput(props: MultiSelectProps) {
  const {
    id,
    description,
    label,
    error,
    isInvalid,
    isDisabled,
    isRequired,
    options,
    size: _size,
    onChange,
    value,
  } = props;

  const collection = useMemo(
    () =>
      createListCollection({
        items: Object.entries(options).map(([value, label]) => ({
          label,
          value,
        })),
      }),
    [options],
  );

  const selectedLabels = useMemo(() => {
    if (!value || value.length === 0) return '';
    return value.map((val) => options[val as keyof typeof options]).join(', ');
  }, [value, options]);

  return (
    <InputContainer
      id={id}
      description={description}
      label={label}
      error={error}
      isInvalid={isInvalid}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Select.Root
        multiple
        collection={collection}
        disabled={isDisabled}
        invalid={isInvalid}
        value={value}
        onValueChange={(val) => onChange(id!, val.value)}
        closeOnSelect={false}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger {...selectTriggerStyles}>
            <Select.ValueText
              placeholder="Select options"
              color={value && value.length > 0 ? 'brand.white' : 'text.placeholder'}
            >
              {selectedLabels}
            </Select.ValueText>
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content {...selectContentStyles}>
              <VStack gap="0" align="stretch">
                {collection.items.map((item, index) => {
                  const isChecked = value?.includes(item.value) || false;
                  return (
                    <Box key={item.value}>
                      <Select.Item
                        item={item}
                        {...selectItemBaseStyles}
                        cursor={isDisabled ? 'not-allowed' : 'pointer'}
                        _hover={{
                          bg: isDisabled ? 'brand.tealDark' : 'select.hover',
                        }}
                      >
                        <Box
                          w="20px"
                          h="20px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                          color="brand.white"
                        >
                          {isChecked ? <FaRegCheckSquare size={20} /> : <FaRegSquare size={20} />}
                        </Box>
                        <Text variant="body" color="brand.white" flex="1 0 0">
                          {item.label}
                        </Text>
                        <Select.ItemIndicator display="none" />
                      </Select.Item>
                      {index < collection.items.length - 1 && (
                        <Separator borderColor="select.separator" borderWidth="1px" h="0" m="0" />
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </InputContainer>
  );
}
